import React, { useContext, useEffect, useState } from 'react';
import { FaChartBar } from 'react-icons/fa';
import api from '../../../api';
import { AuthContext } from '../../../components/AuthContext';
import DeliveryOrdersReports from '../../company-head/reports/DeliveryOrderReport';
import DeliveryOrdersDetailedReports from '../../company-head/reports/DeliveryOrdersDetailedReports';
import QuotationsReports from '../../company-head/reports/QuotationsReports';
import ProformaInvoiceReports from '../../company-head/reports/ProformaInvoiceReports';
import ChoiceEntryReports from '../../company-head/reports/ChoiceEntryReports';
import GoodsReturnReports from '../../company-head/reports/GoodsReturnReports';
import TallyReports from '../../company-head/reports/TallyReports';
import ManagementDashboard from '../../company-head/Dashboard';

// Keys must match the access keys used by the company head dashboard reports,
// plus 'management-dashboard' for the company head default dashboard export.
const REPORT_COMPONENTS = {
  'management-dashboard': { label: 'Management Dashboard', Component: ManagementDashboard, props: { showHeader: false } },
  'delivery-orders-reports': { label: 'Delivery Orders', Component: DeliveryOrdersReports, props: { readOnly: true } },
  'delivery-orders-detailed-reports': { label: 'Delivery Orders (Detailed)', Component: DeliveryOrdersDetailedReports },
  'quotations-reports': { label: 'Quotations', Component: QuotationsReports },
  'proforma-invoice-reports': { label: 'Proforma Invoices', Component: ProformaInvoiceReports },
  'choice-entry-reports': { label: 'Choice Entry', Component: ChoiceEntryReports },
  'goods-return-reports': { label: 'Goods Return', Component: GoodsReturnReports },
  'tally-reports': { label: 'Tally Reports', Component: TallyReports },
};

// Keep the Management Dashboard first when it is part of the selection.
const dashboardFirst = (list) => {
  const idx = list.findIndex((r) => r.key === 'management-dashboard');
  if (idx <= 0) return list;
  const next = [...list];
  const [dash] = next.splice(idx, 1);
  next.unshift(dash);
  return next;
};

const ManagementReports = () => {
  const { user, activeDomainConfig, currentDomain } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeKey, setActiveKey] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      const company = user?.user?.company;
      const companyId = typeof company === 'string' ? company : company?._id;
      if (!companyId) {
        setError('No company is associated with your account.');
        setLoading(false);
        return;
      }
      try {
        const res = await api.get(`/companies/${companyId}`);
        const settings = res.data?.managementReportSettings;
        const configured = settings && Array.isArray(settings.reports) ? settings.reports : [];
        let enabled = [];
        if (configured.length > 0) {
          // Explicit selection saved for the company.
          enabled = configured
            .filter((r) => r && r.enabled !== false && REPORT_COMPONENTS[r.key])
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        } else {
          // Nothing configured - follow the company head dashboard's selected reports.
          const headAccess =
            activeDomainConfig?.companyHeadDashboardAccess ||
            company?.companyHeadDashboardAccess ||
            (() => {
              const domains = res.data?.domains || [];
              const match =
                domains.find((d) => d.domainName === currentDomain) || domains[0];
              return match?.companyHeadDashboardAccess || [];
            })();
          enabled = Object.keys(REPORT_COMPONENTS)
            .filter((key) => headAccess.includes(key))
            .map((key) => ({ key, label: REPORT_COMPONENTS[key].label }));
        }
        enabled = dashboardFirst(enabled);
        setReports(enabled);
        setActiveKey(enabled[0]?.key || '');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load management reports.');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [user, activeDomainConfig, currentDomain]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-500 dark:text-gray-400">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" />
        <span className="ml-2">Loading reports...</span>
      </div>
    );
  }

  if (error || reports.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200 mb-3">
          <FaChartBar />
          <h1 className="text-lg font-semibold">Management Reports</h1>
        </div>
        <div className="p-4 bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-700 rounded text-sm text-gray-600 dark:text-gray-300">
          {error || 'No management reports are configured for this company yet.'}
        </div>
      </div>
    );
  }

  const active = REPORT_COMPONENTS[activeKey];
  const Active = active?.Component;

  return (
    <div className="p-4">
      <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
        {reports.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setActiveKey(r.key)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              activeKey === r.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {r.label || REPORT_COMPONENTS[r.key].label}
          </button>
        ))}
      </div>
      {Active ? <Active {...(active?.props || {})} /> : null}
    </div>
  );
};

export default ManagementReports;
